import React, { useCallback, useRef } from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import useIsomorphicLayoutEffect from "use-isomorphic-layout-effect";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import * as styles from "./OfferingPanels.module.css";
import classnames from "classnames/bind";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import { useMediaQuery } from "react-responsive";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import Media from "@qlik/qlik/src/components/Media";

const cx = classnames.bind(styles);

const OfferingPanels = ({ data }) => {
    const { title, additionalConfiguration, cardItems = [], customId } = data ?? {};
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration);

    // Store refs for each panel in an array
    const panelRefs = useRef([]);

    // The container for the panels
    const panelContainerRef = useRef(null);

    // Store dimensions – used to set the CSS variables for the panel widths and heights
    const dimensions = useRef({
        panelWidth: false,
        sideWidth: false,
        totalHeight: false,
    });

    // Define whether the panels are in their "default state" (the load-in state)
    const isDefault = useRef(true);

    const togglePanels = (clickedPanel, otherPanel) => {
        if (
            clickedPanel.classList.contains("isDefault") &&
            !clickedPanel.classList.contains("isActive") &&
            !clickedPanel.classList.contains("isInactive")
        ) {
            // First interaction –- retaining both isActive and isDefault
            clickedPanel.classList.add("isActive");
        } else {
            // Second interaction -- removing isDefault
            // Adjust classes for clicked panel
            clickedPanel.classList.remove("isDefault");
            clickedPanel.classList.add("isActive");
            clickedPanel.classList.remove("isInactive");

            // Add disabling attributes to button element
            const clickedPanelButton = clickedPanel.querySelector("button");
            clickedPanelButton.setAttribute("disabled", "true");
            clickedPanelButton.setAttribute("aria-disabled", "true");
            clickedPanelButton.setAttribute("aria-pressed", "true");
            clickedPanelButton.setAttribute("aria-expanded", "true");
        }

        // Adjust classes for the other panel
        otherPanel.classList.remove("isDefault");
        otherPanel.classList.remove("isActive");
        otherPanel.classList.add("isInactive");

        // Remove disabling attributes from button element
        const otherPanelButton = otherPanel.querySelector("button");
        otherPanelButton.removeAttribute("disabled");
        otherPanelButton.setAttribute("aria-disabled", "false");
        otherPanelButton.setAttribute("aria-pressed", "false");
        otherPanelButton.setAttribute("aria-expanded", "false");
    };

    // Function to measure and set the panel container's height based on the tallest panel
    const equalizeHeight = () => {
        // Remove existing CSS variable
        panelContainerRef.current.style.removeProperty("--v-panelHeight");

        requestAnimationFrame(() => {
            const panels = panelRefs.current.filter((panel) => panel); // Filter out null refs
            if (panels.length === 0) return; // Exit if no panels are available

            // Calculate the tallest panel height
            const panelHeights = panels.map((panel) => panel.offsetHeight);
            const tallestPanelHeight = Math.max(...panelHeights);

            // Update the CSS variable for the panel container
            dimensions.current.totalHeight = tallestPanelHeight;

            if (panelContainerRef?.current && dimensions?.current?.totalHeight)
                panelContainerRef.current.style.setProperty("--v-panelHeight", `${dimensions.current.totalHeight}px`);
        });
    };

    // Establish the sidePanel widths based on the total panel width
    const equalizeWidths = () => {
        // Remove existing CSS variable
        panelContainerRef.current.style.removeProperty("--v-panelWidth");

        requestAnimationFrame(() => {
            const firstPanel = panelRefs?.current?.[0];
            const firstPanelWidth = firstPanel.clientWidth;

            // Set the panelWidth and sideWidth CSS variables
            dimensions.current.panelWidth = firstPanelWidth;
            dimensions.current.sideWidth = Math.ceil(firstPanelWidth / 2);

            // Update the CSS variables for the panel container
            panelContainerRef.current.style.setProperty("--v-panelWidth", `${dimensions.current.panelWidth}px`);
            panelContainerRef.current.style.setProperty("--v-sideWidth", `${dimensions.current.sideWidth}px`);
        });
    };

    // Func to kick off width and height equalization
    const equalizeDimensions = () => {
        if (!panelContainerRef.current) return; // Exit if the panel container ref is null

        panelContainerRef.current.style.setProperty("--v-transitionProperties", "none");

        requestAnimationFrame(() => {
            equalizeHeight();
            equalizeWidths();
        });

        // Re-enable transition properties after a short delay
        setTimeout(() => {
            panelContainerRef.current.style.removeProperty("--v-transitionProperties");
        }, 10);
    };

    // Resets layout by removing active classes and resetting CSS variables
    const resetLayout = useCallback(() => {
        // Remove active classes from all panels
        for (const panel of panelRefs.current) {
            if (!panel) continue; // Skip if the panel ref is null

            panel.classList.remove("isActive");
            panel.classList.remove("isInactive");
            panel.classList.add("isDefault");

            panel.removeAttribute("aria-disabled");
            panel.removeAttribute("aria-pressed");
            panel.removeAttribute("aria-expanded");

            const panelButton = panel.querySelector("button");
            if (panelButton) {
                // Skip if the button ref is null
                panelButton.removeAttribute("disabled");
                panelButton.removeAttribute("aria-disabled");
                panelButton.removeAttribute("aria-pressed");
            }
        }

        // Reset the panel grid to its default state
        if (panelContainerRef.current) {
            panelContainerRef.current.style.removeProperty("--v-panelGrid");
            panelContainerRef.current.style.removeProperty("--v-panelWidth");
            panelContainerRef.current.style.removeProperty("--v-panelHeight");
            panelContainerRef.current.style.removeProperty("--v-sideWidth");
        }
    }, []);

    // Responsive media query for device detection
    const isDesktop = useMediaQuery({ query: "(min-width: 992px)" }, undefined);

    const handlePanelClick = useCallback(
        (panelNo) => {
            if (!isDesktop) return; // Exit if not desktop
            if (isDesktop) {
                // Establish the initial CSS var state for manipulation later
                if (isDefault) panelContainerRef.current.style.setProperty("--v-panelGrid", "50% 50%");

                isDefault.current = false; // Set default state to false

                // Add active classes to the clicked panel
                const clickedPanel = panelRefs.current[panelNo - 1];

                // Get the panel which was NOT clicked
                // If panel 1 clicked, other is panel 2; if panel 2 clicked, other is panel 1
                const otherPanelIndex = panelNo === 1 ? 1 : 0;
                const otherPanel = panelRefs.current[otherPanelIndex];

                if (clickedPanel) {
                    // Swap the attributes and classes
                    togglePanels(clickedPanel, otherPanel);

                    // Left side activation
                    if (panelNo % 2 !== 0) {
                        panelContainerRef.current.style.setProperty("--v-panelGrid", "75% 25%");
                    } else {
                        // Right side activation
                        panelContainerRef.current.style.setProperty("--v-panelGrid", "25% 75%");
                    }
                }
            }
        },
        [isDesktop]
    );

    useIsomorphicLayoutEffect(() => {
        // Create a debounced resize handler
        let resizeTimeout;

        const handleResize = () => {
            // Reset the layout
            resetLayout();

            // Clear any existing timeout
            if (resizeTimeout) clearTimeout(resizeTimeout);

            // Use setTimeout to debounce resize events
            resizeTimeout = setTimeout(() => {
                // Equalize dimensions only if it's desktop
                if (isDesktop) equalizeDimensions();
            }, 100); // 150ms debounce
        };

        // Initial call
        handleResize();

        // Event listeners
        window.addEventListener("load", handleResize);
        window.addEventListener("resize", handleResize);

        // Cleanup
        return () => {
            window.removeEventListener("load", handleResize);
            window.removeEventListener("resize", handleResize);
            if (resizeTimeout) clearTimeout(resizeTimeout);
        };
    }, [isDesktop]); // Only depend on isDesktop

    // Panel Body component
    const PanelBody = ({ data }) => {
        const { buttons, title, eyebrowText, description, media } = data;

        return (
            <div className={`${cx("panelBody")}`}>
                <div className={cx("panelBodyInterior")}>
                    <div className={`${cx("panelMedia")}`}>
                        {media && (
                            <Media
                                media={media}
                                className={cx("panelImage")}
                                alt={media?.title}
                                title={media?.title}
                                width={510}
                                height={305}
                            />
                        )}
                    </div>

                    <div className={`${cx("panelBottom")}`}>
                        <div className={`${cx("panelCopy")}`}>
                            <div className="panelEyebrow">
                                {eyebrowText && <p className="block tag-2 uppercase pb-4">{eyebrowText}</p>}
                            </div>

                            <div className={cx("panelDescription")}>
                                <h3>{title}</h3>
                                <p>{description}</p>
                            </div>
                        </div>

                        <div className={cx("panelCTAs")}>
                            <div>
                                {buttons &&
                                    buttons.length > 0 &&
                                    buttons.map((cta) => (
                                        <Cta key={cta.id} data={cta} className={cx("panelCta")} isFullWidth />
                                    ))}
                            </div>
                        </div>
                    </div>

                    <div className={cx("panelCross")} />
                </div>
            </div>
        );
    };

    // Side Panel component
    const SidePanel = ({ body }) => {
        // Render the body content
        const renderedBody = renderRichText(body, formatContentfulRichText());

        return (
            <div className={`${cx("sidePanel")}`}>
                {renderedBody && renderRichText(body, formatContentfulRichText())}
            </div>
        );
    };

    // Primary Offering Panel component - both the Panel Body and the Side Pane
    const OfferingPanel = ({ data, panelNo }) => {
        const panelBodyContent = {
            eyebrowText: data?.eyebrowText,
            title: data?.title,
            description: data?.description?.description,
            media: data?.media?.[0],
            buttons: data?.cta,
            body: data?.body,
        };

        const panelWrapperClasses = {
            "transition-all duration-300": true,
            rounded: true,
            isLeft: panelNo % 2 !== 0,
            isRight: panelNo % 2 === 0,
            isDefault: isDefault.current,
        };

        const panelClasses = {
            "bg-white": true,
            "flex flex-row flex-nowrap": true,
        };

        return (
            <div
                ref={(el) => {
                    if (el) panelRefs.current[panelNo - 1] = el;
                }}
                className={cx("panelWrapper", panelWrapperClasses)}
            >
                <button
                    className={cx(panelClasses, "panel")}
                    type="button"
                    tabIndex={0}
                    onClick={() => handlePanelClick(panelNo)} // Handle click without re-rendering
                    onKeyUp={(e) => {
                        if (e.key === "Enter" || e.key === " ") handlePanelClick(panelNo);
                    }}
                >
                    {panelNo % 2 !== 0 && (
                        <>
                            <SidePanel body={data.body} />
                            <PanelBody data={panelBodyContent} />
                        </>
                    )}
                    {panelNo % 2 === 0 && (
                        <>
                            <PanelBody data={panelBodyContent} />
                            <SidePanel body={data.body} />
                        </>
                    )}
                </button>
            </div>
        );
    };

    return (
        <section className={`offeringPanels bg-stone-light ${sectionSpacingClassName}`} id={customId}>
            {title && (
                <div className="container pt-15 lg:pt-30">
                    <div className="text-center">
                        <h2
                            className={cx("title")}
                            dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                        />
                    </div>
                </div>
            )}

            <div className="container pt-16 pb-32 lg:pt-20 lg:pb-40 ">
                <div className={`${cx("panelContainer")}`} ref={panelContainerRef}>
                    {cardItems.map((item, index) => (
                        <OfferingPanel key={`${item.contentful_id}`} data={item} panelNo={index + 1} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default OfferingPanels;
